<!-- SwipFx • Order Details (Bootstrap 5) -->
<div class="min-vh-100 bg-light">
  <app-header [page]="page"></app-header>

  <main class="container py-4">

    <!-- Top bar -->
    <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
      <div>
        <h4 class="mb-0 fw-bold">Order Details</h4>
        <div class="text-muted small">View order status, user, holds, and execution legs</div>
      </div>

      <div class="d-flex gap-2">
        <button class="btn btn-outline-primary btn-sm" (click)="goBack()">
          <i class="bi bi-arrow-left"></i> Back
        </button>
        <button class="btn btn-outline-primary btn-sm" [disabled]="loading" (click)="loadOrder()">
          <span *ngIf="!loading">Refresh</span>
          <span *ngIf="loading" class="spinner-border spinner-border-sm"></span>
        </button>
        <button class="btn btn-outline-danger btn-sm" *ngIf="canManageOrder()" [disabled]="cancellingOrder"
          (click)="cancelOrder()">

          <span *ngIf="!cancellingOrder">
            Cancel Remaining
          </span>

          <span *ngIf="cancellingOrder" class="spinner-border spinner-border-sm">
          </span>

        </button>
      </div>
    </div>


    <!-- ERROR -->
    <div *ngIf="error" class="alert alert-danger">{{ error }}</div>

    <!-- LOADING SKELETON -->
    <div *ngIf="loading && !orderData" class="card shadow-sm border-0">
      <div class="card-body p-4">
        <div class="placeholder-glow">
          <div class="placeholder col-4 mb-2"></div>
          <div class="placeholder col-7 mb-4"></div>
          <div class="placeholder col-12 mb-2"></div>
          <div class="placeholder col-10 mb-2"></div>
          <div class="placeholder col-8"></div>
        </div>
      </div>
    </div>

    <div *ngIf="orderData" class="row g-3">
      <!-- LEFT: Summary + Meta -->
      <div class="col-12 col-lg-4">
        <!-- Summary Card -->
        <div class="card shadow-sm border-0 mb-3">
          <div class="card-body">
            <div class="d-flex align-items-start justify-content-between gap-2">
              <div>
                <div class="fw-bold">#{{orderData.id}}</div>
                <div class="text-muted small">Created: {{orderData.createdAt}}</div>
                <div class="text-muted small">Last Updated: {{orderData.lastUpdatedAt}}</div>
              </div>
              <span [ngClass]="getOrderStatusClass(orderData.status)">{{orderData.status}}</span>
            </div>

            <hr class="my-3" />

            <div class="d-flex justify-content-between small text-muted">
              <span>Pair</span>
              <span class="fw-semibold">{{orderData.fromCurrency.code}} → {{orderData.toCurrency.code}}</span>
            </div>

            <div class="d-flex justify-content-between small text-muted mt-2">
              <span>Rate</span>
              <span class="fw-semibold">{{ orderData.rate | smartAmount:orderData.toCurrency.symbol }} /
                {{orderData.fromCurrency.symbol}}</span>
            </div>

            <div class="d-flex justify-content-between small text-muted mt-2">
              <span>Original Amount</span>
              <span class="fw-semibold">{{ orderData.originalAmount | smartAmount:orderData.fromCurrency.symbol
                }}</span>
            </div>

            <div class="d-flex justify-content-between small text-muted mt-2">
              <span>Remaining</span>
              <span class="fw-semibold">{{ orderData.remainingAmount | smartAmount:orderData.fromCurrency.symbol
                }}</span>
            </div>

            <hr class="my-3" />

            <div class="small text-muted mb-1">Progress</div>
            <div class="progress" style="height: 8px;">
              <div class="progress-bar" role="progressbar" [style.width.%]="progrwessPercent()"></div>
            </div>
            <div class="d-flex justify-content-between small text-muted mt-2">
              <span>Filled</span>
              <span class="fw-semibold">{{progrwessPercent() | number : '1.0-0'}}%</span>
            </div>
          </div>
        </div>

        <div class="card shadow-sm border-0 mb-3" *ngIf="canManageOrder()">

          <div class="card-body">

            <div class="fw-bold mb-3">
              Update Rate
            </div>

            <div class="text-muted small mb-3">
              Changing the rate affects only the unmatched portion of this order.
            </div>

            <label class="form-label small">
              New Rate
            </label>

            <input type="number" class="form-control" step="0.00000001" [(ngModel)]="newRate">

            <button class="btn btn-primary btn-sm mt-3" [disabled]="updatingRate" (click)="updateRate()">

              <span *ngIf="!updatingRate">
                Update Rate
              </span>

              <span *ngIf="updatingRate" class="spinner-border spinner-border-sm">
              </span>

            </button>
          </div>
        </div>

        <!-- Currency Details -->
        <div class="card shadow-sm border-0">
          <div class="card-body">
            <div class="fw-bold mb-2">Currencies</div>

            <div class="border rounded-3 p-3 bg-light-subtle">
              <div class="d-flex justify-content-between">
                <div class="fw-semibold">From</div>
                <span class="badge bg-body text-muted border">{{orderData.fromCurrency.code}}</span>
              </div>
              <div class="text-muted small">{{orderData.fromCurrency.name}} ({{orderData.fromCurrency.symbol}})</div>
              <div class="small text-muted mt-2 d-flex justify-content-between">
                <span>Min order</span><span class="fw-semibold">{{ orderData.fromCurrency.minOrder | smartAmount
                  }}</span>
              </div>
            </div>

            <div class="border rounded-3 p-3 bg-light-subtle mt-3">
              <div class="d-flex justify-content-between">
                <div class="fw-semibold">To</div>
                <span class="badge bg-body text-muted border">{{orderData.toCurrency.code}}</span>
              </div>
              <div class="text-muted small">{{orderData.toCurrency.name}} ({{orderData.toCurrency.symbol}})</div>
              <div class="small text-muted mt-2 d-flex justify-content-between">
                <span>Min order</span><span class="fw-semibold">{{ orderData.toCurrency.minOrder | smartAmount }}</span>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- RIGHT: Tabs -->
      <div class="col-12 col-lg-8">
        <div class="card shadow-sm border-0">
          <div class="card-body p-0">
            <div class="alert alert-secondary border-0 shadow-sm" *ngIf="isRateChangedParent()">

              <div class="fw-bold mb-1">
                Rate Changed
              </div>

              <div class="small">
                This order was partially fulfilled, and the remaining unmatched amount was moved into a new order with
                an updated rate.
              </div>
            </div>

            <div class="alert alert-info border-0 shadow-sm" *ngIf="isPartiallyCancelled()">

              <div class="fw-bold mb-1">
                Partially Cancelled
              </div>

              <div class="small">
                This order was partially fulfilled, and the remaining unmatched amount was cancelled.
              </div>
            </div>

            <div class="alert alert-light border shadow-sm" *ngIf="hasParentOrder()">

              <div class="fw-bold mb-1">
                Created From Previous Order
              </div>

              <div class="small mb-2">
                This order was created from the remaining unmatched amount of a previous order.
              </div>

              <button class="btn btn-outline-primary btn-sm" type="button" (click)="openParentOrder()">
                View Parent Order
              </button>
            </div>

            <div class="card border-0 shadow-sm mb-3" *ngIf="childOrders.length > 0">
              <div class="card-body">

                <div class="d-flex align-items-center justify-content-between mb-3">
                  <div class="fw-bold">
                    Child Orders
                  </div>

                  <span class="badge bg-primary">
                    {{ childOrders.length }}
                  </span>
                </div>

                <div class="text-muted small mb-3">
                  These orders were created from the remaining unmatched portion of this order after a rate change.
                </div>

                <div class="table-responsive">
                  <table class="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Amount</th>
                        <th>Remaining</th>
                        <th>Rate</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>

                    <tbody>

                      <tr *ngFor="let child of childOrders">

                        <td class="fw-semibold">
                          {{ child.id }}
                        </td>

                        <td>
                          {{ child.originalAmount | smartAmount:child.fromCurrency.symbol }}
                        </td>

                        <td>
                          {{ child.remainingAmount | smartAmount:child.fromCurrency.symbol }}
                        </td>

                        <td>
                          {{ child.rate | smartAmount:child.toCurrency.symbol }}
                        </td>

                        <td>
                          <span [ngClass]="getOrderStatusClass(child.status)">
                            {{ child.status }}
                          </span>
                        </td>

                        <td class="text-end">
                          <button class="btn btn-outline-primary btn-sm" (click)="openChildOrder(child.id)">
                            View
                          </button>
                        </td>

                      </tr>

                    </tbody>
                  </table>
                </div>

              </div>
            </div>

            <div class="card border-0 shadow-sm mb-3" *ngIf="orderLineage.length > 1">
              <div class="card-body">
                <div class="fw-bold mb-3">Order History</div>

                <div class="list-group list-group-flush">
                  <button type="button" class="list-group-item list-group-item-action" *ngFor="let item of orderLineage"
                    (click)="openLineageOrder(item.id)">

                    <div class="d-flex justify-content-between align-items-start gap-2">
                      <div>
                        <div class="fw-semibold">
                          {{ item.fromCurrency.code }} → {{ item.toCurrency.code }}
                        </div>

                        <div class="text-muted small">
                          {{ item.originalAmount | smartAmount:item.fromCurrency.symbol }}
                          at rate
                          {{ item.rate | smartAmount:item.toCurrency.symbol }}
                        </div>

                        <div class="text-muted small" *ngIf="item.closeReason">
                          {{ item.closeReason }}
                        </div>
                      </div>

                      <span [ngClass]="getOrderStatusClass(item.status)">
                        {{ item.status }}
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            <!-- Tabs -->
            <ul class="nav nav-tabs px-3 pt-3" role="tablist">
              <li class="nav-item" role="presentation">
                <button class="nav-link active" data-bs-toggle="tab" data-bs-target="#tabOverview" type="button"
                  role="tab">
                  Overview
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button class="nav-link" data-bs-toggle="tab" data-bs-target="#tabUser" type="button" role="tab">
                  User
                </button>
              </li>
              <li class="nav-item" role="presentation" *ngIf="isAdminPage">
                <button class="nav-link" data-bs-toggle="tab" data-bs-target="#tabUserMatched" type="button" role="tab">
                  User Matched
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button class="nav-link" data-bs-toggle="tab" data-bs-target="#tabHolds" type="button" role="tab">
                  Wallet Holds
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button class="nav-link" data-bs-toggle="tab" data-bs-target="#tabLegs" type="button" role="tab">
                  Legs
                </button>
              </li>
            </ul>

            <div class="tab-content p-3">
              <!-- OVERVIEW -->
              <div class="tab-pane fade show active" id="tabOverview" role="tabpanel">
                <div class="row g-3">
                  <div class="col-12 col-md-6">
                    <div class="border rounded-3 p-3 bg-white">
                      <div class="fw-semibold mb-2">Order</div>
                      <div class="small text-muted d-flex justify-content-between">
                        <span>ID</span><span class="fw-semibold">{{orderData.id}}</span>
                      </div>
                      <div class="small text-muted d-flex justify-content-between mt-2">
                        <span>Status</span><span
                          [ngClass]="getOrderStatusClass(orderData.status)">{{orderData.status}}</span>
                      </div>
                      <div class="small text-muted d-flex justify-content-between mt-2">
                        <span>Created</span><span class="fw-semibold">{{orderData.createdAt | date : 'medium'}}</span>
                      </div>
                      <div class="small text-muted d-flex justify-content-between mt-2">
                        <span>Updated</span><span class="fw-semibold">{{orderData.lastUpdatedAt | date :
                          'medium'}}</span>
                      </div>
                      <div class="d-flex justify-content-between small mt-2" *ngIf="orderData.closedAt">
                        <span class="text-muted">Closed At</span>
                        <span>{{ orderData.closedAt | date:'medium' }}</span>
                      </div>

                      <div class="d-flex justify-content-between small mt-2" *ngIf="orderData.closeReason">
                        <span class="text-muted">Close Reason</span>
                        <span>{{ orderData.closeReason }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-md-6">
                    <div class="border rounded-3 p-3 bg-white">
                      <div class="fw-semibold mb-2">Amounts</div>
                      <div class="small text-muted d-flex justify-content-between">
                        <span>Original</span><span class="fw-semibold">{{ orderData.originalAmount |
                          smartAmount:orderData.fromCurrency.symbol }}</span>
                      </div>
                      <div class="small text-muted d-flex justify-content-between mt-2">
                        <span>Remaining</span><span class="fw-semibold">{{ orderData.remainingAmount |
                          smartAmount:orderData.fromCurrency.symbol }}</span>
                      </div>
                      <div class="small text-muted d-flex justify-content-between mt-2">
                        <span>Rate</span><span class="fw-semibold">{{ orderData.rate |
                          smartAmount:orderData.toCurrency.symbol }} / {{orderData.fromCurrency.symbol}}</span>
                      </div>
                      <div class="small text-muted d-flex justify-content-between mt-2">
                        <span>Pair</span><span class="fw-semibold">{{orderData.fromCurrency.code}} →
                          {{orderData.toCurrency.code}}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- USER -->
              <div class="tab-pane fade" id="tabUser" role="tabpanel">
                <div class="border rounded-3 p-3 bg-white">
                  <div class="d-flex align-items-center justify-content-between">
                    <div class="fw-semibold">{{orderData.user.lastName + ', ' + orderData.user.firstName}}</div>
                    <span [class]="getUserStatusClass(orderData.user.status)">{{orderData.user.status}}</span>
                  </div>
                  <div class="text-muted small mt-1">{{orderData.user.role}} Since: {{orderData.user.createdAt| date :
                    'medium'}}</div>

                  <hr class="my-3" />

                  <div class="row g-2 small">
                    <div class="col-12 col-md-6 d-flex justify-content-between">
                      <span class="text-muted">User ID</span><span class="fw-semibold">{{orderData.user.id}}</span>
                    </div>
                    <div class="col-12 col-md-6 d-flex justify-content-between">
                      <span class="text-muted">Status</span><span class="fw-semibold">{{orderData.user.status}}</span>
                    </div>
                    <div class="col-12 col-md-6 d-flex justify-content-between">
                      <span class="text-muted">Email</span><span class="fw-semibold">{{orderData.user.email}}</span>
                    </div>
                    <div class="col-12 col-md-6 d-flex justify-content-between">
                      <span class="text-muted">Phone</span><span class="fw-semibold">{{orderData.user.phone}}</span>
                    </div>
                    <div class="col-12 col-md-6 d-flex justify-content-between">
                      <span class="text-muted">Country</span><span class="fw-semibold">{{orderData.user.country}}</span>
                    </div>
                    <div class="col-12 col-md-6 d-flex justify-content-between">
                      <span class="text-muted">Created</span><span class="fw-semibold">{{orderData.user.createdAt| date
                        : 'medium'}}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- MATCHED USER -->
              <div class="tab-pane fade" id="tabUserMatched" role="tabpanel" *ngIf="isAdminPage">
                <div class="border rounded-3 p-3 bg-white">
                  <div class="d-flex align-items-center justify-content-between">
                    <div class="fw-semibold">{{orderData.user.lastName + ', ' + orderData.user.firstName}}</div>
                    <span [class]="getUserStatusClass(orderData.user.status)">{{orderData.user.status}}</span>
                  </div>
                  <div class="text-muted small mt-1">{{orderData.user.role}} Since: {{orderData.user.createdAt| date :
                    'medium'}}</div>

                  <hr class="my-3" />

                  <div class="row g-2 small">
                    <div class="col-12 col-md-6 d-flex justify-content-between">
                      <span class="text-muted">User ID</span><span class="fw-semibold">{{orderData.user.id}}</span>
                    </div>
                    <div class="col-12 col-md-6 d-flex justify-content-between">
                      <span class="text-muted">Status</span><span class="fw-semibold">{{orderData.user.status}}</span>
                    </div>
                    <div class="col-12 col-md-6 d-flex justify-content-between">
                      <span class="text-muted">Email</span><span class="fw-semibold">{{orderData.user.email}}</span>
                    </div>
                    <div class="col-12 col-md-6 d-flex justify-content-between">
                      <span class="text-muted">Phone</span><span class="fw-semibold">{{orderData.user.phone}}</span>
                    </div>
                    <div class="col-12 col-md-6 d-flex justify-content-between">
                      <span class="text-muted">Country</span><span class="fw-semibold">{{orderData.user.country}}</span>
                    </div>
                    <div class="col-12 col-md-6 d-flex justify-content-between">
                      <span class="text-muted">Created</span><span class="fw-semibold">{{orderData.user.createdAt| date
                        : 'medium'}}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- HOLDS -->
              <div class="tab-pane fade" id="tabHolds" role="tabpanel">
                <div class="table-responsive">
                  <table class="table table-hover align-middle mb-0">
                    <thead class="table-light">
                      <tr class="small text-muted">
                        <th style="min-width:220px;">Hold ID</th>
                        <th style="min-width:220px;">Wallet ID</th>
                        <th class="text-end" style="min-width:120px;">Amount</th>
                        <th style="min-width:120px;">Status</th>
                        <th style="min-width:160px;">Created</th>
                        <th style="min-width:160px;">Released</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let hold of orderData.walletHolds">
                        <td class="fw-semibold">{{hold.id}}</td>
                        <td class="text-muted"><a
                            [routerLink]="['/wallets/manage', hold.walletId, 'orders', orderId]">{{hold.walletId}}</a>
                        </td>
                        <td class="text-end fw-semibold">{{ hold.amount | smartAmount }}</td>
                        <td><span [class]="getHoldStatusClass(hold.status)">{{hold.status}}</span></td>
                        <td class="text-muted small">{{hold.createdAt| date : 'medium'}}</td>
                        <td class="text-muted small">{{hold.releasedAt| date : 'medium'}}</td>
                      </tr>
                      <tr *ngIf="orderData.walletHolds.length === 0">
                        <td colspan="7" class="text-center text-muted small">No holds found for this order.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- LEGS -->
              <div class="tab-pane fade" id="tabLegs" role="tabpanel">
                <div class="table-responsive">
                  <table class="table table-hover align-middle mb-0">
                    <thead class="table-light">
                      <tr class="small text-muted">
                        <th style="min-width:220px;">Leg ID</th>
                        <th style="min-width:120px;">Match ID</th>
                        <th style="min-width:140px;">Pair</th>
                        <th class="text-end" style="min-width:140px;">Send</th>
                        <th class="text-end" style="min-width:140px;">Receive</th>
                        <th class="text-end" style="min-width:120px;">Rate</th>
                        <th style="min-width:160px;">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let leg of orderData.legs">
                        <td class="fw-semibold">{{leg.id}}</td>
                        <td class="fw-semibold">{{leg.matchId}}</td>
                        <td class="fw-semibold">{{leg.fromCurrency.code}} → {{leg.toCurrency.code}}</td>
                        <td class="text-end">{{ leg.sendAmount | smartAmount }}</td>
                        <td class="text-end">{{ leg.receiveAmount | smartAmount }}</td>
                        <td class="text-end">{{ leg.rate | smartAmount }}</td>
                        <td class="text-muted small">{{leg.createdAt| date : 'medium'}}</td>
                      </tr>
                      <tr *ngIf="orderData.legs.length === 0">
                        <td colspan="8" class="text-center text-muted small">No execution legs found for this order.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="small text-muted mt-3">
                  Legs represent execution path segments for the order (e.g., direct, path2, path3).
                </div>
              </div>
            </div>
          </div>
        </div>
        <app-support-context-card type="order" title="Need help with orders?"
          subtitle="Get quick answers about matching, delays, and order status.">
        </app-support-context-card>
      </div>
    </div>

    <app-footer></app-footer>
  </main>
</div>